// tools/index.js
// Core Comprehend Live tool set. Every tool here must stay documented in
// docs/comprehend-live.md (repo root). Handlers return plain JSON — the
// runtime forwards it to the model as function_call_output.
import { liveStore, QUEUE_KINDS, scoreToGrade } from '../liveStore';
import { focusTracker } from '../focus';
import { audioRouter, ROUTES } from '../audioRouter';
import * as recorderBridge from '../recorderBridge';
import { getEMRAdapter, emrIsMock } from '../emrBridge';

// Action choreography (~1.5s end to end): the chat ducks first, the card
// pulses while "applying" with the queue exposed, then the card exits.
const DUCK_MS = 350;
const STEP_BEAT_MS = 200; // breathing room between steps so eyes catch up
const MIN_WORKING_MS = 1400;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const patientHint = {
  type: 'string',
  description:
    "Patient first/last name as spoken, e.g. 'Alice' or 'Margaret Torres'. Omit to use the most recently discussed card.",
};

/**
 * Shared one-click-fix path used by both the panel's "Fix note" button and
 * the apply_note_fix voice tool.
 */
export const applyQueueItemFix = async (apiClient, item, extraInstruction) => {
  if (!item) {
    return {
      error: 'Could not tell which card you meant — say the patient name.',
    };
  }
  focusTracker.touchQueueItem(item);
  // Sequence: duck the chat, pulse the card, THEN let it exit — paced so
  // the whole thing is watchable even when the backend answers instantly.
  const start = Date.now();
  const ducked = liveStore.pingQueueSpotlight(item.id);
  liveStore.updateQueueItem(item.id, { status: 'working' });
  if (ducked) await sleep(DUCK_MS + STEP_BEAT_MS);
  const instruction =
    extraInstruction ||
    item.proposedText ||
    `Address this compliance gap: ${item.title}`;
  try {
    const rsp = await apiClient.agentApplyNoteFix(
      item.conversationId,
      instruction,
    );
    if (rsp?.error) throw new Error(rsp.error);
    const minVisible = (ducked ? DUCK_MS + STEP_BEAT_MS : 0) + MIN_WORKING_MS;
    const elapsed = Date.now() - start;
    if (elapsed < minVisible) await sleep(minVisible - elapsed);
    await sleep(STEP_BEAT_MS); // settle beat before the slide-away
    liveStore.updateQueueItem(item.id, {
      status: 'resolved',
      undo: rsp?.undo || null,
      detail: 'Applied to note',
    });
    return {
      ok: true,
      patientName: item.patientName,
      applied: instruction,
      fieldName: rsp?.fieldName,
    };
  } catch (error) {
    liveStore.updateQueueItem(item.id, { status: 'pending' });
    return { error: `Could not apply the fix: ${error.message}` };
  }
};

/** Dismiss with the same duck-then-exit pacing. */
export const dismissQueueItemAnimated = async (item) => {
  if (!item) return { error: 'Could not tell which card you meant.' };
  const ducked = liveStore.pingQueueSpotlight(item.id);
  if (ducked) await sleep(DUCK_MS + STEP_BEAT_MS + 250);
  liveStore.updateQueueItem(item.id, { status: 'dismissed' });
  return { ok: true, dismissed: item.title };
};

/**
 * Undo a previously applied fix using the undo payload returned by the
 * backend ({fieldName, previous}).
 */
export const undoQueueItemFix = async (apiClient, item) => {
  if (!item?.undo?.fieldName)
    return { error: 'Nothing to undo for that card.' };
  try {
    const rsp = await apiClient.editConversation(
      item.conversationId,
      item.undo.fieldName,
      item.undo.previous ?? '',
    );
    if (rsp?.error) throw new Error(rsp.error);
    liveStore.updateQueueItem(item.id, {
      status: 'pending',
      undo: null,
      detail: 'Fix undone',
    });
    return { ok: true };
  } catch (error) {
    return { error: `Undo failed: ${error.message}` };
  }
};

/**
 * Build the full core tool set.
 * @param {Object} deps
 * @param {Object} deps.apiClient - authenticated ApiClient
 * @param {Function} deps.navigate - react-router navigate
 */
export const createCoreTools = ({ apiClient, navigate }) => {
  const resolveItem = (args, kind) =>
    focusTracker.resolveQueueItem(liveStore, {
      itemId: args?.itemId,
      patientName: args?.patientName,
      kind,
    });

  const applyFixToItem = (item, extraInstruction) =>
    applyQueueItemFix(apiClient, item, extraInstruction);

  return [
    {
      type: 'function',
      name: 'apply_note_fix',
      description:
        "Apply the agent's proposed fix for a nudge card to the patient's note ('fix it', 'add it', 'accept that rec for Alice'). One-click apply with undo.",
      parameters: {
        type: 'object',
        properties: {
          patientName: patientHint,
          itemId: { type: 'string', description: 'Queue item id if known.' },
          instruction: {
            type: 'string',
            description:
              'Optional clinician dictation to apply instead of the proposed text, e.g. "Shoulder flexion AROM was 110 degrees".',
          },
        },
        required: [],
      },
      handler: async (args) =>
        applyFixToItem(
          resolveItem(args, QUEUE_KINDS.NUDGE) || resolveItem(args),
          args?.instruction,
        ),
    },
    {
      type: 'function',
      name: 'dictate_correction',
      description:
        "Route a spoken statement into a specific patient's note ('Jimmy's ROM was 110', 'Alice did stairs with min assist'). Works across patients in one breath — call once per patient.",
      parameters: {
        type: 'object',
        properties: {
          patientName: patientHint,
          statement: {
            type: 'string',
            description:
              'The clinical statement to add to the note, verbatim clinical content only.',
          },
        },
        required: ['statement'],
      },
      handler: async (args) => {
        const session = focusTracker.resolvePatientGlobal(
          liveStore,
          apiClient,
          args?.patientName,
        );
        if (!session?.conversationId) {
          return { error: `No visit found for "${args?.patientName}" today.` };
        }
        focusTracker.touchPatient(session.patientName);
        const taskId = liveStore.addTask(
          `Adding dictation to ${session.patientName}'s note`,
          {
            conversationId: session.conversationId,
            patientName: session.patientName,
          },
        );
        try {
          const rsp = await apiClient.agentApplyNoteFix(
            session.conversationId,
            args.statement,
          );
          if (rsp?.error) throw new Error(rsp.error);
          liveStore.resolveTask(taskId, 'Dictation added to note');
          return { ok: true, patientName: session.patientName };
        } catch (error) {
          liveStore.failTask(taskId, `Dictation failed: ${error.message}`);
          return { error: error.message };
        }
      },
    },
    {
      type: 'function',
      name: 'explain_compliance_score',
      description:
        "Explain why a note got its compliance grade ('why did I get a B on his note?'). Returns the met/partial/missing breakdown.",
      parameters: {
        type: 'object',
        properties: { patientName: patientHint },
        required: [],
      },
      handler: async (args) => {
        const session = focusTracker.resolvePatientGlobal(
          liveStore,
          apiClient,
          args?.patientName,
        );
        if (!session) return { error: 'Which patient do you mean?' };
        if (!session.conversationId) {
          return {
            error: `${session.patientName} has no visit today, so there is no note to explain.`,
          };
        }
        focusTracker.touchPatient(session.patientName);
        const details = await apiClient.getConversationDetails(
          session.conversationId,
        );
        if (!details || details.error) {
          return { error: 'Could not load that note.' };
        }
        return {
          patientName: session.patientName,
          score: details.score,
          grade: scoreToGrade(details.score),
          requirements: (details.requirement_evaluations || []).map((e) => ({
            requirement: e.requirement_text,
            status: e.status,
            required: Boolean(e.is_required),
            suggestion: e.suggestion || null,
          })),
        };
      },
    },
    {
      type: 'function',
      name: 'suggest_clinical_nudge',
      description:
        "Add a clinical-suggestion nudge card to the queue — a recommendation for the clinician to consider, e.g. 'suggest the Hawkins-Kennedy test for Jimmy', 'remind me to ask Margaret about her RTM', 'recommend a special test to rule out frozen shoulder'. Use when the clinician asks for ideas / special tests / recommendations for a patient. If you include proposedText, the card gets a one-tap apply.",
      parameters: {
        type: 'object',
        properties: {
          patientName: patientHint,
          title: {
            type: 'string',
            description:
              "Short, scannable headline for the suggestion, e.g. 'Try Hawkins-Kennedy to rule out impingement'.",
          },
          detail: {
            type: 'string',
            description:
              'Optional one-line rationale or context for the suggestion.',
          },
          proposedText: {
            type: 'string',
            description:
              'Optional note text to add if the clinician accepts (enables one-tap apply). Omit for an FYI-only nudge.',
          },
        },
        required: ['title'],
      },
      handler: async (args) => {
        const session = args?.patientName
          ? focusTracker.resolvePatientGlobal(
              liveStore,
              apiClient,
              args.patientName,
            )
          : null;
        if (args?.patientName && !session) {
          return { error: `No patient matching "${args.patientName}" found.` };
        }
        if (session?.patientName)
          focusTracker.touchPatient(session.patientName);
        liveStore.addQueueItem({
          kind: QUEUE_KINDS.NUDGE,
          source: 'agent',
          severity: 'info',
          conversationId: session?.conversationId || null,
          patientName: session?.patientName || null,
          title: args.title,
          detail: args.detail || null,
          proposedText: args.proposedText || null,
        });
        return {
          ok: true,
          patientName: session?.patientName || null,
          title: args.title,
        };
      },
    },
    {
      type: 'function',
      name: 'dismiss_card',
      description: "Dismiss a card from the queue ('dismiss that', 'skip it').",
      parameters: {
        type: 'object',
        properties: { patientName: patientHint, itemId: { type: 'string' } },
        required: [],
      },
      handler: async (args) => {
        const item = resolveItem(args);
        return dismissQueueItemAnimated(item);
      },
    },
    {
      type: 'function',
      name: 'get_patient_summary',
      description:
        "Summarize a patient: lifecycle (visit count, recent notes), today's status. Use for 'anything I should know about Margaret?'",
      parameters: {
        type: 'object',
        properties: { patientName: patientHint },
        required: [],
      },
      handler: async (args) => {
        const session = focusTracker.resolvePatientGlobal(
          liveStore,
          apiClient,
          args?.patientName,
        );
        if (!session) return { error: 'Which patient do you mean?' };
        focusTracker.touchPatient(session.patientName);
        if (!session.group) {
          return {
            patientName: session.patientName,
            todayStatus: session.status,
            note: 'No patient chart linked to this visit.',
          };
        }
        const history = await apiClient.getPatientHistory(session.group, 3);
        return {
          patientName: session.patientName,
          todayStatus: session.status,
          grade: session.grade,
          history: history && !history.error ? history : null,
        };
      },
    },
    {
      type: 'function',
      name: 'open_view',
      description:
        "Navigate the app ('open Margaret's visit', 'show my schedule', 'open settings').",
      parameters: {
        type: 'object',
        properties: {
          view: {
            type: 'string',
            enum: [
              'home',
              'visits',
              'visit_details',
              'patients',
              'multi_recorder',
              'settings',
            ],
          },
          patientName: patientHint,
        },
        required: ['view'],
      },
      handler: async (args) => {
        const paths = {
          home: '/User/home',
          visits: '/User/conversations',
          patients: '/User/patients',
          multi_recorder: '/User/conversations/multi-recorder',
          settings: '/User/settings',
        };
        if (args.view === 'visit_details') {
          const session = focusTracker.resolvePatient(
            liveStore,
            args?.patientName,
          );
          if (!session?.conversationId)
            return { error: 'Which patient do you mean?' };
          navigate(`/User/conversations/details#${session.conversationId}`);
          return { ok: true, opened: `${session.patientName}'s visit` };
        }
        const path = paths[args.view];
        if (!path) return { error: `Unknown view ${args.view}` };
        navigate(path);
        return { ok: true, opened: args.view };
      },
    },
    {
      type: 'function',
      name: 'toggle_visit_recording',
      description:
        "Start or stop recording for a visit ('start recording Margaret', 'stop the session').",
      parameters: {
        type: 'object',
        properties: {
          action: { type: 'string', enum: ['start', 'stop'] },
          patientName: patientHint,
        },
        required: ['action'],
      },
      handler: async (args) => {
        const session = focusTracker.resolvePatientGlobal(
          liveStore,
          apiClient,
          args?.patientName,
        );
        const ids = session?.conversationId
          ? [session.conversationId]
          : recorderBridge.getRecordingIds();
        return args.action === 'start'
          ? recorderBridge.startRecording(ids)
          : recorderBridge.stopRecording(ids);
      },
    },
    {
      type: 'function',
      name: 'set_dictate_target',
      description:
        "Enter dictate mode: route the clinician's mic to exactly one visit ('dictate to Margaret's visit'). Pass no patient to exit dictate mode.",
      parameters: {
        type: 'object',
        properties: { patientName: patientHint },
        required: [],
      },
      handler: async (args) => {
        if (!args?.patientName) {
          audioRouter.setRoute(ROUTES.SESSIONS);
          liveStore.setDictateTarget(null);
          return { ok: true, mode: 'sessions' };
        }
        const session = focusTracker.resolvePatientGlobal(
          liveStore,
          apiClient,
          args.patientName,
        );
        if (!session?.conversationId)
          return { error: `No visit found for "${args.patientName}" today.` };
        audioRouter.setRoute(ROUTES.DICTATE, {
          conversationId: session.conversationId,
        });
        liveStore.setDictateTarget(session.conversationId);
        return { ok: true, mode: 'dictate', patientName: session.patientName };
      },
    },
    {
      type: 'function',
      name: 'push_note_to_emr',
      description:
        "Queue a finished note to be pushed to the clinician's EMR as a long-running background task ('push Wong's note to the EMR').",
      parameters: {
        type: 'object',
        properties: { patientName: patientHint },
        required: [],
      },
      handler: async (args) => {
        const session = focusTracker.resolvePatientGlobal(
          liveStore,
          apiClient,
          args?.patientName,
        );
        if (!session?.conversationId)
          return { error: 'Which patient do you mean?' };
        const taskId = liveStore.addTask(
          `Pushing ${session.patientName}'s note to EMR`,
          {
            conversationId: session.conversationId,
            patientName: session.patientName,
          },
        );
        try {
          const rsp = await getEMRAdapter().pushNote(
            session.conversationId,
            session.patientName,
          );
          if (!rsp?.ok) throw new Error(rsp?.error || 'EMR push failed');
          liveStore.resolveTask(
            taskId,
            emrIsMock()
              ? 'Pushed (Mock EMR) — sign in your EMR'
              : 'Pushed — open your EMR to sign',
          );
          return { ok: true, queued: session.patientName, mock: emrIsMock() };
        } catch (error) {
          liveStore.failTask(taskId, `EMR push failed: ${error.message}`);
          return { error: error.message };
        }
      },
    },
    {
      type: 'function',
      name: 'find_patient',
      description:
        "Search the clinician's whole patient panel by name — works with no visit open ('get me up to speed on Annie'). Returns the match plus whether they have a visit today.",
      parameters: {
        type: 'object',
        properties: {
          patientName: { type: 'string', description: 'Name as spoken.' },
        },
        required: ['patientName'],
      },
      handler: async (args) => {
        const session = focusTracker.resolvePatientGlobal(
          liveStore,
          apiClient,
          args.patientName,
        );
        if (!session) {
          return { error: `No patient matching "${args.patientName}" found.` };
        }
        focusTracker.touchPatient(session.patientName);
        return {
          patientName: session.patientName,
          hasVisitToday: Boolean(session.conversationId),
          todayStatus: session.status,
          group: session.group || null,
        };
      },
    },
    {
      type: 'function',
      name: 'get_recent_patients',
      description:
        "The clinician's most recently seen patients ('load my most recent patient', 'who did I see last?'). Works from a cold open.",
      parameters: { type: 'object', properties: {}, required: [] },
      handler: async () => {
        const data = await apiClient.getUserConversations();
        const conversations =
          data && !data.error && data.conversations ? data.conversations : [];
        const seen = new Set();
        const recent = [];
        conversations
          .slice()
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .forEach((c) => {
            const name = (c.conversationName || '').split(' - ')[0];
            if (name && !seen.has(name)) {
              seen.add(name);
              recent.push({ patientName: name, lastVisit: c.createdAt });
            }
          });
        if (recent.length) focusTracker.touchPatient(recent[0].patientName);
        return { recentPatients: recent.slice(0, 5) };
      },
    },
    {
      type: 'function',
      name: 'list_queue',
      description:
        'Fresh snapshot of the live panel state: sessions recording, pending nudges/actions/reviews. Use before answering questions about the queue.',
      parameters: { type: 'object', properties: {}, required: [] },
      handler: async () => liveStore.getAgentContextSnapshot(),
    },
  ];
};

/**
 * Reasoning trace for read-only tools — rendered as faint step lines in the
 * chat so the clinician can SEE the agent working ("pulled Jimmy's history")
 * without it claiming an action happened.
 */
export const stepForToolCall = (name, args, result) => {
  if (result?.error) return null; // errors surface via the model's reply
  switch (name) {
    case 'find_patient':
      return `looked up ${result.patientName}`;
    case 'get_patient_summary':
      return `pulled ${result.patientName || 'the patient'}'s history`;
    case 'explain_compliance_score':
      return `reviewed ${result.patientName}'s score breakdown`;
    case 'get_recent_patients':
      return 'checked recent patients';
    case 'list_queue':
      return 'scanned the queue';
    default:
      return null;
  }
};

/**
 * Friendly receipt text for mutating tools — the provider posts these into
 * the chat ledger on every executed tool call. Read-only tools return null
 * (no receipt; the model's answer is the artifact).
 */
export const receiptForToolCall = (name, args, result) => {
  if (result?.error) {
    return {
      text: `Couldn't complete ${name.replace(/_/g, ' ')}: ${result.error}`,
    };
  }
  switch (name) {
    case 'apply_note_fix':
      return {
        text: `Applied fix to ${result.patientName || 'the'} note`,
        why: result.applied,
      };
    case 'dictate_correction':
      return {
        text: `Added dictation to ${result.patientName}'s note`,
        why: args?.statement,
      };
    case 'suggest_clinical_nudge':
      return {
        text: result.patientName
          ? `Added a clinical nudge for ${result.patientName}`
          : 'Added a clinical nudge',
        why: result.title,
      };
    case 'dismiss_card':
      return { text: `Dismissed: ${result.dismissed}` };
    case 'set_dictate_target':
      return result.mode === 'dictate'
        ? { text: `Dictate mode — mic routed to ${result.patientName} only` }
        : { text: 'Dictate mode off — mic back to all sessions' };
    case 'toggle_visit_recording':
      return { text: 'Recording updated' };
    case 'push_note_to_emr':
      return { text: `${result.queued}'s note queued for EMR push` };
    default:
      return null; // read-only tools: no receipt
  }
};

export default createCoreTools;
